import React, { useState } from "react";
import { Box, CircularProgress, Rating, Tooltip, useMediaQuery } from "@mui/material";
import { useNavigate } from "react-router-dom";
import {
  PrecisionManufacturing as MachineIcon,
  ChevronRight as ChevronRightIcon,
  PublishOutlined,
  LocalOfferOutlined,
  HourglassBottomRounded as WaitingIcon,
  AssignmentTurnedInOutlined,
  BuildOutlined,
  CheckCircle as CheckCircleIcon,
  StarBorder,
  Star as StarFilled,
  ScheduleOutlined,
  DescriptionOutlined as FileIcon,
  ArchiveOutlined,
  UnarchiveOutlined,
  CalendarTodayOutlined,
} from "@mui/icons-material";
import useTranslation from "../../../shared/hooks/useTranslation";
import { ServiceRequest, ServiceRequestStatusCode } from "../models/models";
import { createStatusLabelMap } from "../utils/statusMaps";
import { dt, StatusBadgeColorConfig } from "../../../shared/themes/redesignTokens";
import RedesignStatusBadge from "../../../shared/components/redesign/RedesignStatusBadge";
import ServiceLifecycleTracker from "../../shared/components/ServiceLifecycleTracker";
import { getOwnerLifecycleStages } from "../../shared/utils/lifecycleStages";
import { getOwnerGuidanceCategory } from "../../shared/utils/guidanceCategories";
import ContextGuidanceBox from "../../shared/components/ContextGuidanceBox";
import TimeInStageRow from "../../shared/components/TimeInStageRow";
import ContextInfoRow from "../../shared/components/ContextInfoRow";
import receivedOffersApi from "../api/receivedOffersApi";
import serviceExecutionApi from "../api/serviceExecutionApi";
import dayjs from "dayjs";
import { formatUiDate } from "../../../shared/utils/dateFormat";

// ===================================================================
//  Helpers
// ===================================================================

const formatFileSize = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

/**
 * formatTimeAgo — returns a human-readable "time ago" string.
 * Uses i18n translate function for localization (servicing namespace).
 */
const formatTimeAgo = (
  isoDate: string | Date | undefined,
  translate: (key: string, opts?: any) => any
): string | null => {
  if (!isoDate) return null;
  const now = dayjs();
  const then = dayjs(isoDate);
  const diffMin = now.diff(then, "minute");
  if (diffMin < 1) return translate("servicing:card.timeAgo.justNow");
  if (diffMin < 60) return translate("servicing:card.timeAgo.minutesAgo", { count: diffMin });
  const diffHr = now.diff(then, "hour");
  if (diffHr < 24) return translate("servicing:card.timeAgo.hoursAgo", { count: diffHr });
  const diffDay = now.diff(then, "day");
  if (diffDay < 7) return translate("servicing:card.timeAgo.daysAgo", { count: diffDay });
  return formatUiDate(then, "dayMonth");
};

/**
 * getOwnerTimeIndicator — returns the relevant "time in stage" date
 * and a label key for the current SR status (owner perspective).
 */
const getOwnerTimeIndicator = (
  item: ServiceRequest,
): { date?: string | Date; labelKey: string } => {
  const S = ServiceRequestStatusCode;
  switch (item.statusCode) {
    case S.draft:
      return { date: item.createdDate, labelKey: "servicing:card.timeAgo.created" };
    case S.openForOffers:
      return { date: item.openDate || item.createdDate, labelKey: "servicing:card.timeAgo.published" };
    case S.negotiating:
      return { date: item.modifiedDate, labelKey: "servicing:card.timeAgo.since" };
    case S.awaitingProviderConfirmation:
      return { date: item.modifiedDate, labelKey: "servicing:card.timeAgo.accepted" };
    case S.inProgress:
      return { date: item.acceptedOffer?.confirmedArrivalDate || item.modifiedDate, labelKey: "servicing:card.timeAgo.started" };
    case S.awaitingCompletionConfirmation:
      return { date: item.acceptedOffer?.workCompletedDate || item.modifiedDate, labelKey: "servicing:card.timeAgo.completed" };
    case S.completed:
      return { date: item.modifiedDate, labelKey: "servicing:card.timeAgo.finished" };
    case S.archived:
      return { date: item.modifiedDate, labelKey: "servicing:card.timeAgo.archived" };
    default:
      return { date: item.modifiedDate, labelKey: "servicing:card.timeAgo.updated" };
  }
};

// ===================================================================
//  STATUS COLORS (SR perspective / owner view)
// ===================================================================

export const srStatusBadgeColors: Record<number, StatusBadgeColorConfig> = {
  [ServiceRequestStatusCode.draft]: { bg: "#F9EBEA", text: "#C0392B", border: "#F5B7B1" },
  [ServiceRequestStatusCode.openForOffers]: { bg: "#FEF9E7", text: "#F39C12", border: "#F9E79F" },
  [ServiceRequestStatusCode.negotiating]: { bg: "#F5EEF8", text: "#8E44AD", border: "#D2B4DE" },
  [ServiceRequestStatusCode.inProgress]: { bg: "#EBF5FB", text: dt.blueHover, border: "#AED6F1" },
  [ServiceRequestStatusCode.completed]: { bg: "#EAFAF1", text: "#27AE60", border: "#ABEBC6" },
  [ServiceRequestStatusCode.archived]: { bg: "#F4F6F9", text: "#6B7280", border: "#D1D5DB" },
  [ServiceRequestStatusCode.awaitingProviderConfirmation]: { bg: "#FEF9E7", text: "#F39C12", border: "#F9E79F" },
  [ServiceRequestStatusCode.awaitingCompletionConfirmation]: { bg: "#FDF2E9", text: "#E67E22", border: "#F5CBA7" },
};

// ===================================================================
//  Left border accent color per SR status
// ===================================================================

export const leftBorderColors: Record<number, string> = {
  [ServiceRequestStatusCode.draft]: "#D1D5DB",
  [ServiceRequestStatusCode.openForOffers]: "#F59E0B",
  [ServiceRequestStatusCode.negotiating]: "#8B5CF6",
  [ServiceRequestStatusCode.inProgress]: "#2563EB",
  [ServiceRequestStatusCode.completed]: "#059669",
  [ServiceRequestStatusCode.archived]: "#D1D5DB",
  [ServiceRequestStatusCode.awaitingProviderConfirmation]: "#F59E0B",
  [ServiceRequestStatusCode.awaitingCompletionConfirmation]: "#E67E22",
};

// ===================================================================
//  Props
// ===================================================================

interface ServiceRequestCardProps {
  item: ServiceRequest;
  index: number;
  formatCurrency: (value: number) => string;
  onClick: (item: ServiceRequest) => void;
  onPublish?: (item: ServiceRequest) => void;
  onViewOffers?: (item: ServiceRequest) => void;
  onConfirmCompletion?: (item: ServiceRequest) => void;
  onLeaveReview?: (item: ServiceRequest) => void;
  onArchive?: (item: ServiceRequest) => void;
  onUnarchive?: (item: ServiceRequest) => void;
}

// ===================================================================
//  CTA Button
// ===================================================================

interface CTAConfig {
  label: string;
  color: string;
  hoverColor: string;
  onClick?: () => void;
  icon?: React.ReactNode;
  outline?: boolean;
  pulse?: boolean;
  isIndicator?: boolean;
  compact?: boolean;
}

const CTAButton: React.FC<CTAConfig> = ({ label, color, hoverColor, onClick, icon, outline, pulse, isIndicator, compact }) => (
  <Box
    onClick={(e) => {
      e.stopPropagation();
      onClick?.();
    }}
    sx={{
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      gap: "6px",
      background: outline || isIndicator
        ? (isIndicator ? `${color}14` : "transparent")
        : `linear-gradient(135deg, ${color}, ${hoverColor})`,
      color: outline || isIndicator ? color : "#FFFFFF",
      border: outline ? `1.5px solid ${color}` : isIndicator ? `1px solid ${color}33` : "none",
      borderRadius: "24px",
      padding: "10px 16px",
      fontSize: "12px",
      fontWeight: 700,
      fontFamily: dt.font,
      cursor: isIndicator ? "default" : "pointer",
      // L6 (USER-LANG-IT-PL-1): may wrap. nowrap made DE "Aus dem Archiv wiederherstellen"
      // a 278px pill (EN 205px) that forced the 30% right zone wider than its share.
      whiteSpace: "normal",
      textAlign: "center",
      lineHeight: 1.3,
      maxWidth: "100%",
      transition: "all 0.2s ease",
      width: compact ? "auto" : "100%",
      boxSizing: "border-box",
      ...(pulse && { animation: "offerRing 2s ease-in-out infinite" }),
      ...(!isIndicator && {
        "&:hover": {
          transform: "translateY(-1px)",
          ...(outline
            ? { backgroundColor: `${color}14`, borderColor: hoverColor }
            : { boxShadow: `0 4px 12px ${color}40` }),
        },
      }),
    }}
  >
    {icon}
    {label}
    {!isIndicator && <ChevronRightIcon sx={{ fontSize: "14px" }} />}
  </Box>
);

// ===================================================================
//  Label Row style constants
// ===================================================================

const LABEL_WIDTH = "90px";
const labelSx = {
  fontSize: "11px",
  fontWeight: 600,
  textTransform: "uppercase" as const,
  letterSpacing: "0.8px",
  color: dt.textMuted,
  fontFamily: dt.font,
  // L6 (USER-LANG-IT-PL-1): wraps inside the fixed 90px lane instead of running under the
  // value (PL "FIRMA SERWISOWA" +35px, DE "ARBEITSBERICHT" +19px; EN "WORK REPORT" was +4px).
  whiteSpace: "normal" as const,
  overflowWrap: "break-word" as const,
  WebkitHyphens: "auto" as const,
  hyphens: "auto" as const,
  width: LABEL_WIDTH,
  minWidth: LABEL_WIDTH,
  flexShrink: 0,
};

// ===================================================================
//  Guidance text helper (owner perspective)
// ===================================================================

export const getGuidanceInfo = (
  item: ServiceRequest,
  translate: (key: string, options?: any) => any,
): { text: string; boldName?: string } | null => {
  const S = ServiceRequestStatusCode;
  const providerName = item.acceptedOffer?.providerName || "";

  switch (item.statusCode) {
    case S.draft:
      return { text: translate("servicing:card.guidance.draft") };
    case S.openForOffers:
      if ((item.offersCount ?? 0) > 0) {
        return {
          text: translate("servicing:card.guidance.openWithOffers", { count: item.offersCount }),
        };
      }
      return { text: translate("servicing:card.guidance.openNoOffers") };
    case S.negotiating:
      return {
        text: translate("servicing:card.guidance.negotiating", { offersCount: item.offersCount ?? 0 }),
      };
    case S.awaitingProviderConfirmation:
      return {
        text: translate("servicing:card.guidance.awaitingConfirmation", { provider: providerName }),
        boldName: providerName,
      };
    case S.inProgress: {
      // If confirmed arrival date exists, show the scheduled message. Otherwise "working" message.
      if (item.acceptedOffer?.confirmedArrivalDate) {
        const confDate = formatUiDate(item.acceptedOffer.confirmedArrivalDate);
        const tw = item.acceptedOffer.confirmedArrivalTimeFrom && item.acceptedOffer.confirmedArrivalTimeTo
          ? ` \u00B7 ${item.acceptedOffer.confirmedArrivalTimeFrom.slice(0, 5)}\u2013${item.acceptedOffer.confirmedArrivalTimeTo.slice(0, 5)}`
          : "";
        return {
          text: translate("servicing:card.guidance.inProgress", { provider: providerName, date: confDate, timeWindow: tw }),
          boldName: providerName,
        };
      }
      return {
        text: translate("servicing:card.guidance.inProgressWorking", { provider: providerName }),
        boldName: providerName,
      };
    }
    case S.awaitingCompletionConfirmation:
      return {
        text: translate("servicing:card.guidance.awaitingCompletion", { provider: providerName }),
        boldName: providerName,
      };
    case S.completed:
      if (item.acceptedOffer?.reviewRating != null) {
        return { text: translate("servicing:card.guidance.completedWithReview") };
      }
      return { text: translate("servicing:card.guidance.completedNoReview") };
    case S.archived:
      return { text: translate("servicing:card.guidance.archived") };
    default:
      return null;
  }
};

// GuidanceText rendering is now handled by ContextGuidanceBox (shared component)

// ===================================================================
//  ServiceRequestCard
// ===================================================================

const ServiceRequestCard: React.FC<ServiceRequestCardProps> = ({
  item,
  index,
  formatCurrency,
  onClick,
  onPublish,
  onViewOffers,
  onConfirmCompletion,
  onLeaveReview,
  onArchive,
  onUnarchive,
}) => {
  const { translate } = useTranslation(["servicing", "myMachinery", "common"]);
  const navigate = useNavigate();
  const isPhone = useMediaQuery("(max-width:768px)");
  const S = ServiceRequestStatusCode;

  const c = srStatusBadgeColors[item.statusCode] || srStatusBadgeColors[S.archived];
  const statusLabelMap = createStatusLabelMap(translate);
  const borderColor = leftBorderColors[item.statusCode] || "#D1D5DB";

  const isArchived = item.statusCode === S.archived;
  const hasAcceptedOffer = !!item.acceptedOffer;
  const offersCount = item.offersCount ?? 0;
  const newOffersCount = item.newOffersCount ?? 0;
  const hasReview = item.acceptedOffer?.reviewRating != null;

  // File download/view state
  const [downloadingFile, setDownloadingFile] = useState<string | null>(null);

  // Resolve file content as data URI (fetches full offer if content missing)
  const resolveFileContent = async (fileName: string): Promise<string> => {
    if (!item.acceptedOffer?.offerId) return "";
    const fullOffer = await receivedOffersApi.getReceivedOffer(item.id, item.acceptedOffer.offerId);
    const file = fullOffer.files?.find(f => f.name === fileName);
    if (!file?.content) return "";

    const content = file.content;
    // Content can be: a URL (https://...), a data URI (data:...), or raw base64
    if (content.startsWith("http://") || content.startsWith("https://") || content.startsWith("data:")) {
      return content;
    }
    // Raw base64 — prepend data URI prefix
    const ext = fileName.split(".").pop()?.toLowerCase() || "";
    const mimeTypes: Record<string, string> = {
      pdf: "application/pdf", png: "image/png", jpg: "image/jpeg", jpeg: "image/jpeg",
      gif: "image/gif", webp: "image/webp", doc: "application/msword",
      docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      xls: "application/vnd.ms-excel", xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    };
    const mime = mimeTypes[ext] || "application/octet-stream";
    return `data:${mime};base64,${content}`;
  };

  // View file: opens in new tab (same pattern as machinery ReportFile.tsx)
  const handleFileView = async (e: React.MouseEvent, fileName: string) => {
    e.stopPropagation();
    if (downloadingFile) return;
    setDownloadingFile(fileName);
    try {
      const content = await resolveFileContent(fileName);
      if (!content) return;
      const link = document.createElement("a");
      link.href = content;
      link.target = "_blank";
      link.click();
      link.remove();
    } catch (err) {
      console.error("Failed to view file:", err);
    } finally {
      setDownloadingFile(null);
    }
  };

  // Resolve completion file content (fetches from completion files endpoint)
  const resolveCompletionFileContent = async (fileName: string): Promise<string> => {
    const files = await serviceExecutionApi.getCompletionFiles(item.id);
    const file = files?.find(f => f.name === fileName);
    if (!file?.content) return "";

    const content = file.content;
    // Content can be: a URL (https://...), a data URI (data:...), or raw base64
    if (content.startsWith("http://") || content.startsWith("https://") || content.startsWith("data:")) {
      return content;
    }
    // Raw base64 — prepend data URI prefix
    const ext = fileName.split(".").pop()?.toLowerCase() || "";
    const mimeTypes: Record<string, string> = {
      pdf: "application/pdf", png: "image/png", jpg: "image/jpeg", jpeg: "image/jpeg",
      gif: "image/gif", webp: "image/webp", doc: "application/msword",
      docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      xls: "application/vnd.ms-excel", xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    };
    const mime = mimeTypes[ext] || "application/octet-stream";
    return `data:${mime};base64,${content}`;
  };

  // View completion file: opens in new tab
  const handleCompletionFileView = async (e: React.MouseEvent, fileName: string) => {
    e.stopPropagation();
    if (downloadingFile) return;
    setDownloadingFile(fileName);
    try {
      const content = await resolveCompletionFileContent(fileName);
      if (!content) return;
      const link = document.createElement("a");
      link.href = content;
      link.target = "_blank";
      link.click();
      link.remove();
    } catch (err) {
      console.error("Failed to view completion file:", err);
    } finally {
      setDownloadingFile(null);
    }
  };

  // Budget display string
  const getBudgetDisplay = (): string => {
    if (item.budgetEnabled) {
      if (item.budgetTypeCode === 1 && item.fixedBudget) {
        return formatCurrency(item.fixedBudget);
      }
      if (item.budgetTypeCode === 2 && item.budgetRangeFrom && item.budgetRangeTo) {
        return `${formatCurrency(item.budgetRangeFrom)}\u2013${formatCurrency(item.budgetRangeTo)}`;
      }
    }
    return "\u2014";
  };

  // Service date display
  const getServiceDateDisplay = (): { text: string; timeWindow?: string } => {
    if (hasAcceptedOffer && item.acceptedOffer?.confirmedArrivalDate) {
      const dateStr = formatUiDate(item.acceptedOffer.confirmedArrivalDate);
      const timeFrom = item.acceptedOffer.confirmedArrivalTimeFrom?.slice(0, 5);
      const timeTo = item.acceptedOffer.confirmedArrivalTimeTo?.slice(0, 5);
      const timeWindow = timeFrom && timeTo ? `${timeFrom}\u2013${timeTo}` : undefined;
      return { text: dateStr, timeWindow };
    }
    if (item.serviceStartDate) {
      return { text: formatUiDate(item.serviceStartDate) };
    }
    return { text: "\u2014" };
  };

  // Price display: BUDGET (pre-acceptance) or AGREED PRICE (post-acceptance)
  const getPriceInfo = (): { label: string; value: string } => {
    if (hasAcceptedOffer && item.acceptedOffer?.totalEstimatedCost) {
      return {
        label: translate("servicing:card.agreedPrice"),
        value: formatCurrency(item.acceptedOffer.totalEstimatedCost),
      };
    }
    return {
      label: translate("servicing:card.budget"),
      value: getBudgetDisplay(),
    };
  };

  // CTA config
  const getCTA = (): CTAConfig | null => {
    switch (item.statusCode) {
      case S.draft:
        return {
          label: translate("servicing:card.publish"),
          color: "#8E44AD",
          hoverColor: "#7D3C98",
          onClick: () => onPublish?.(item),
          icon: <PublishOutlined sx={{ fontSize: "14px" }} />,
        };
      case S.openForOffers:
        if (offersCount > 0) {
          return {
            label: `${translate("servicing:card.viewOffers")} (${offersCount})`,
            color: "#2563EB",
            hoverColor: "#1D4ED8",
            onClick: () => onViewOffers?.(item),
            icon: <LocalOfferOutlined sx={{ fontSize: "14px" }} />,
            pulse: newOffersCount > 0,
          };
        }
        return {
          label: translate("servicing:card.awaitingOffers"),
          color: "#6B7280",
          hoverColor: "#6B7280",
          isIndicator: true,
          icon: <WaitingIcon sx={{ fontSize: "14px", animation: "hourglassSpin 2s linear infinite" }} />,
        };
      case S.negotiating:
        return {
          label: translate("servicing:card.reviewOffers"),
          color: "#8B5CF6",
          hoverColor: "#7C3AED",
          outline: true,
          onClick: () => onViewOffers?.(item),
          icon: <LocalOfferOutlined sx={{ fontSize: "14px" }} />,
        };
      case S.awaitingProviderConfirmation:
        return {
          label: translate("servicing:card.awaitingConfirmation"),
          color: "#F59E0B",
          hoverColor: "#F59E0B",
          isIndicator: true,
          icon: <WaitingIcon sx={{ fontSize: "14px", animation: "hourglassSpin 2s linear infinite" }} />,
        };
      case S.inProgress:
        return {
          label: translate("servicing:card.workInProgress"),
          color: "#2563EB",
          hoverColor: "#2563EB",
          isIndicator: true,
          icon: <BuildOutlined sx={{ fontSize: "14px" }} />,
        };
      case S.awaitingCompletionConfirmation:
        return {
          label: translate("servicing:card.confirmCompletion"),
          color: "#E67E22",
          hoverColor: "#D35400",
          onClick: () => onConfirmCompletion?.(item),
          icon: <AssignmentTurnedInOutlined sx={{ fontSize: "14px" }} />,
          pulse: true,
        };
      case S.completed:
        if (hasReview) {
          return {
            label: translate("servicing:card.archive"),
            color: "#6B7280",
            hoverColor: "#4B5563",
            outline: true,
            onClick: () => onArchive?.(item),
            icon: <ArchiveOutlined sx={{ fontSize: "14px" }} />,
          };
        }
        return {
          label: translate("servicing:card.leaveReview"),
          color: "#059669",
          hoverColor: "#047857",
          outline: true,
          onClick: () => onLeaveReview?.(item),
          icon: <StarBorder sx={{ fontSize: "14px" }} />,
        };
      case S.archived:
        return {
          label: translate("servicing:card.unarchive"),
          color: "#27AE60",
          hoverColor: "#2ECC71",
          onClick: () => onUnarchive?.(item),
          icon: <UnarchiveOutlined sx={{ fontSize: "14px" }} />,
        };
      default:
        return null;
    }
  };

  const cta = getCTA();
  const guidance = getGuidanceInfo(item, translate);
  const priceInfo = getPriceInfo();
  const serviceDateInfo = getServiceDateDisplay();

  // Time-in-stage info for variant C context panel
  const timeIndicator = getOwnerTimeIndicator(item);
  const timeAgoStr = formatTimeAgo(timeIndicator.date, translate);

  // Owner lifecycle stages
  const ownerStages = getOwnerLifecycleStages(item.statusCode, item.acceptedOffer?.statusCode);

  return (
    <Box
      sx={{
        backgroundColor: dt.cardBg,
        borderRadius: dt.radiusSm,
        border: `1px solid ${dt.cardBorder}`,
        borderLeft: `4px solid ${borderColor}`,
        boxShadow: dt.cardShadow,
        overflow: "hidden",
        width: "100%",
        boxSizing: "border-box",
        transition: "all 0.25s cubic-bezier(0.4, 0, 0.2, 1)",
        animation: `cardReveal 0.4s cubic-bezier(0.4, 0, 0.2, 1) ${index * 0.06}s both`,
        "@keyframes cardReveal": {
          "0%": { opacity: 0, transform: "translateY(16px) scale(0.98)" },
          "100%": { opacity: 1, transform: "translateY(0) scale(1)" },
        },
        "@keyframes hourglassSpin": {
          "0%": { transform: "rotate(0deg)" },
          "50%": { transform: "rotate(180deg)" },
          "100%": { transform: "rotate(360deg)" },
        },
        "&:hover": {
          boxShadow: dt.cardShadowHover,
        },
        display: "flex",
        flexDirection: isPhone ? "column" : "row",
        ...(isArchived && { opacity: 0.7 }),
        ...(item.statusCode === S.awaitingCompletionConfirmation && {
          borderLeftColor: "#E67E22",
          animation: `cardReveal 0.4s cubic-bezier(0.4, 0, 0.2, 1) ${index * 0.06}s both, borderPulse 2s ease-in-out infinite`,
          "@keyframes borderPulse": {
            "0%, 100%": { borderLeftColor: "#E67E22" },
            "50%": { borderLeftColor: "#F39C12" },
          },
        }),
      }}
    >
      {/* ================================================================
          LEFT ZONE: header, content rows, footer
          ================================================================ */}
      <Box sx={{ flex: isPhone ? 1 : "7", display: "flex", flexDirection: "column", minWidth: 0 }}>
        {/* Header section (subtle gray bg, inside left zone) */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: isPhone ? "12px 16px" : "12px 24px",
            backgroundColor: "#F8FAFC",
            borderBottom: `1px solid ${dt.cardBorder}`,
          }}
        >
          <Box
            sx={{
              fontWeight: 700,
              fontSize: "16px",
              fontFamily: dt.font,
              color: "#0F172A",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              flex: 1,
              minWidth: 0,
            }}
          >
            {item.issue || "\u2014"}
          </Box>
          <Box
            onClick={(e) => {
              e.stopPropagation();
              onClick(item);
            }}
            sx={{
              fontSize: "12px",
              fontFamily: dt.fontMono,
              color: dt.textLink,
              fontWeight: 600,
              flexShrink: 0,
              ml: "12px",
              cursor: "pointer",
              "&:hover": { textDecoration: "underline" },
            }}
          >
            {item.internalNumber}
          </Box>
        </Box>

        {/* Content section */}
        <Box sx={{ padding: isPhone ? "14px 16px" : "16px 24px", display: "flex", flexDirection: "column", gap: "10px" }}>

            {/* MACHINE */}
            {item.machine && (
              <Box sx={{ display: "flex", alignItems: "center", gap: "10px" }}>
                <Box component="span" sx={labelSx}>
                  {translate("servicing:card.machine")}
                </Box>
                {(item.machine?.imageUrl || item.machine?.catalogImageUrl) ? (
                  <Box
                    component="img"
                    src={item.machine.imageUrl || item.machine.catalogImageUrl}
                    alt=""
                    sx={{
                      width: "40px",
                      height: "40px",
                      borderRadius: dt.radiusSm,
                      border: `1px solid ${dt.cardBorder}`,
                      objectFit: "cover",
                      flexShrink: 0,
                    }}
                  />
                ) : (
                  <Box
                    sx={{
                      width: "40px",
                      height: "40px",
                      borderRadius: dt.radiusSm,
                      border: `1px solid ${dt.cardBorder}`,
                      backgroundColor: dt.inputBg,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}
                  >
                    <MachineIcon sx={{ fontSize: "20px", color: dt.textMuted }} />
                  </Box>
                )}
                <Box sx={{ minWidth: 0 }}>
                  <Box
                    onClick={(e) => {
                      e.stopPropagation();
                      if (item.machine?.id) navigate(`/machinery/${item.machine.id}`);
                    }}
                    sx={{
                      fontSize: "14px",
                      fontWeight: 600,
                      color: dt.textLink,
                      fontFamily: dt.font,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      cursor: "pointer",
                      "&:hover": { textDecoration: "underline" },
                    }}
                  >
                    {item.machine.name}
                  </Box>
                </Box>
              </Box>
            )}

            {/* LOCATION */}
            {(item.locationName || item.parentLocationName) && (
              <Box sx={{ display: "flex", alignItems: "center", gap: "10px" }}>
                <Box component="span" sx={labelSx}>
                  {translate("servicing:location")}
                </Box>
                <Box
                  sx={{
                    fontSize: "13px",
                    fontFamily: dt.font,
                    color: dt.textPrimary,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {item.parentLocationName
                    ? `${item.parentLocationName} > ${item.locationName}`
                    : item.locationName}
                </Box>
              </Box>
            )}

            {/* PROVIDER (when accepted offer exists) or OFFERS (when offers count > 0 and no accepted) */}
            {hasAcceptedOffer && (
              <Box sx={{ display: "flex", alignItems: "center", gap: "10px" }}>
                <Box component="span" sx={labelSx}>
                  {translate("servicing:card.provider")}
                </Box>
                {item.acceptedOffer?.providerLogoUrl ? (
                  <Box
                    component="img"
                    src={item.acceptedOffer.providerLogoUrl}
                    alt=""
                    sx={{
                      width: "32px",
                      height: "32px",
                      borderRadius: "50%",
                      objectFit: "cover",
                      flexShrink: 0,
                      border: `1px solid ${dt.cardBorder}`,
                    }}
                  />
                ) : (
                  <Box
                    sx={{
                      width: "32px",
                      height: "32px",
                      borderRadius: "50%",
                      backgroundColor: dt.blueSoft,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}
                  >
                    <Box sx={{ fontSize: "13px", fontWeight: 700, color: dt.blue, fontFamily: dt.font }}>
                      {item.acceptedOffer?.providerName?.charAt(0)?.toUpperCase() || "?"}
                    </Box>
                  </Box>
                )}
                <Box sx={{ minWidth: 0 }}>
                  <Box
                    onClick={(e) => {
                      e.stopPropagation();
                      if (item.acceptedOffer?.providerId) navigate(`/companies/${item.acceptedOffer.providerId}`);
                    }}
                    sx={{
                      fontSize: "14px",
                      fontWeight: 600,
                      color: dt.textLink,
                      fontFamily: dt.font,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      cursor: "pointer",
                      "&:hover": { textDecoration: "underline" },
                    }}
                  >
                    {item.acceptedOffer?.providerName}
                  </Box>
                  {item.acceptedOffer?.providerAverageRating != null && item.acceptedOffer?.providerReviewCount != null && (
                    <Tooltip
                      title={translate("servicing:reviews.ratingTooltip", {
                        rating: item.acceptedOffer.providerAverageRating.toFixed(1),
                        count: item.acceptedOffer.providerReviewCount,
                      })}
                    >
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.25, mt: "-1px" }}>
                        <Rating
                          value={item.acceptedOffer.providerAverageRating}
                          readOnly
                          precision={0.1}
                          size="small"
                          sx={{ fontSize: "14px", "& .MuiRating-iconFilled": { color: "#FFB400" } }}
                        />
                        <Box sx={{ fontSize: "11px", color: dt.textMuted, fontWeight: 500, fontFamily: dt.font }}>
                          ({item.acceptedOffer.providerReviewCount})
                        </Box>
                      </Box>
                    </Tooltip>
                  )}
                </Box>
              </Box>
            )}

            {!hasAcceptedOffer && offersCount > 0 && !isArchived && (
              <Box sx={{ display: "flex", alignItems: "center", gap: "10px" }}>
                <Box component="span" sx={labelSx}>
                  {translate("servicing:offers")}
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: "6px" }}>
                  <Box
                    sx={{
                      fontSize: "14px",
                      fontWeight: 600,
                      color: dt.textPrimary,
                      fontFamily: dt.font,
                    }}
                  >
                    {translate("servicing:card.offerCount", { count: offersCount })}
                    {item.lowestOfferPrice != null && (
                      <Box component="span" sx={{ color: dt.textSecondary, fontWeight: 400 }}>
                        {" "}&middot; {translate("servicing:card.from")} {formatCurrency(item.lowestOfferPrice)}
                      </Box>
                    )}
                  </Box>
                  {newOffersCount > 0 && (
                    <Box
                      sx={{
                        width: "8px",
                        height: "8px",
                        borderRadius: "50%",
                        backgroundColor: "#E74C3C",
                        boxShadow: "0 0 4px rgba(231, 76, 60, 0.4)",
                        animation: "offerRing 2.5s ease-in-out infinite",
                      }}
                    />
                  )}
                </Box>
              </Box>
            )}

            {/* WORK SUMMARY (when provider completed work — shown for awaiting confirmation + archived) */}
            {item.acceptedOffer?.workCompletionNote && (
              <Box>
                <Box sx={{ fontSize: "10px", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.8px", color: dt.textMuted, fontFamily: dt.font, mb: "4px" }}>
                  {translate("servicing:card.workSummary")}
                </Box>
                <Box
                  sx={{
                    backgroundColor: "#EBF5FB",
                    border: "1px solid #AED6F1",
                    borderLeft: `3px solid ${dt.blueHover}`,
                    borderRadius: "8px",
                    padding: "10px 14px",
                    fontSize: "13px",
                    color: "#1E3A5F",
                    fontFamily: dt.font,
                    lineHeight: 1.4,
                    width: "fit-content",
                    maxWidth: "100%",
                  }}
                >
                  {item.acceptedOffer.workCompletionNote}
                </Box>
              </Box>
            )}

            {/* YOUR REVIEW or DESCRIPTION */}
            {hasReview && item.acceptedOffer ? (
              <Box>
                <Box
                  sx={{
                    backgroundColor: "#FAFDF6",
                    border: "1px solid #D1FAE5",
                    borderLeft: "3px solid #059669",
                    borderRadius: "8px",
                    padding: "12px 14px",
                  }}
                >
                  {/* Label + stars + date row */}
                  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: "6px", flexWrap: "wrap", gap: "4px" }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
                      <Box sx={{ fontSize: "10px", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.8px", color: dt.textSecondary, fontFamily: dt.font }}>
                        {translate("servicing:card.yourReview")}
                      </Box>
                      <Box sx={{ display: "flex", alignItems: "center", gap: "1px" }}>
                        {[1, 2, 3, 4, 5].map((star) => (
                          star <= (item.acceptedOffer?.reviewRating || 0)
                            ? <StarFilled key={star} sx={{ fontSize: "18px", color: "#F59E0B" }} />
                            : <StarBorder key={star} sx={{ fontSize: "18px", color: "#D1D5DB" }} />
                        ))}
                      </Box>
                    </Box>
                    {item.acceptedOffer?.reviewDate && (
                      <Box sx={{ fontSize: "11px", color: dt.textMuted, fontFamily: dt.font }}>
                        {formatUiDate(item.acceptedOffer.reviewDate)}
                      </Box>
                    )}
                  </Box>
                  {/* Review title */}
                  {item.acceptedOffer?.reviewTitle && (
                    <Box sx={{ fontSize: "14px", fontWeight: 700, color: "#0F172A", fontFamily: dt.font, mb: "4px" }}>
                      {item.acceptedOffer.reviewTitle}
                    </Box>
                  )}
                  {/* Review comment */}
                  {item.acceptedOffer?.reviewComment && (
                    <Box
                      sx={{
                        fontSize: "13px",
                        fontWeight: 400,
                        color: "#475569",
                        fontFamily: dt.font,
                        lineHeight: 1.4,
                        position: "relative",
                        maxHeight: "calc(4 * 1.4em)",
                        overflow: "hidden",
                        "&::after": {
                          content: '""',
                          position: "absolute",
                          bottom: 0,
                          left: 0,
                          right: 0,
                          height: "24px",
                          background: "linear-gradient(to bottom, transparent, #FAFDF6)",
                          pointerEvents: "none",
                        },
                      }}
                    >
                      {item.acceptedOffer.reviewComment}
                    </Box>
                  )}
                </Box>
              </Box>
            ) : null}

            {/* OFFER ATTACHMENTS (when accepted offer has files) */}
            {hasAcceptedOffer && item.acceptedOffer?.files && item.acceptedOffer.files.length > 0 && (
              <Box sx={{ display: "flex", alignItems: "flex-start", gap: "10px" }}>
                <Box
                  component="span"
                  sx={{
                    fontSize: "11px",
                    fontWeight: 700,
                    textTransform: "uppercase",
                    letterSpacing: "0.8px",
                    color: dt.textMuted,
                    fontFamily: dt.font,
                    whiteSpace: "nowrap",
                    mt: "2px",
                    minWidth: LABEL_WIDTH,
                    flexShrink: 0,
                  }}
                >
                  {translate("servicing:card.offerAttachments")}
                </Box>
                <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: "4px 12px", minWidth: 0 }}>
                  {item.acceptedOffer.files.map((file, idx) => (
                    <Box
                      key={idx}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: "4px",
                        transition: "all 0.15s ease",
                      }}
                    >
                      {downloadingFile === file.name ? (
                        <CircularProgress size={12} sx={{ color: dt.blue, flexShrink: 0 }} />
                      ) : (
                        <FileIcon sx={{ fontSize: 14, color: dt.textLink, flexShrink: 0 }} />
                      )}
                      <Box
                        onClick={(e) => handleFileView(e, file.name)}
                        sx={{
                          fontSize: "13px",
                          fontWeight: 500,
                          color: dt.textLink,
                          fontFamily: dt.font,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          minWidth: 0,
                          cursor: downloadingFile === file.name ? "wait" : "pointer",
                          textDecoration: "none",
                          "&:hover": { textDecoration: "underline" },
                        }}
                      >
                        {file.name}
                      </Box>
                      <Box sx={{ fontSize: "10px", color: dt.textMuted, fontFamily: dt.fontMono, flexShrink: 0 }}>
                        {formatFileSize(file.size)}
                      </Box>
                    </Box>
                  ))}
                </Box>
              </Box>
            )}

            {/* COMPLETION REPORT ATTACHMENTS (when work completion has files) */}
            {hasAcceptedOffer && item.acceptedOffer?.workCompletionFiles && item.acceptedOffer.workCompletionFiles.length > 0 && (
              <Box sx={{ display: "flex", alignItems: "flex-start", gap: "10px" }}>
                <Box
                  component="span"
                  sx={{
                    fontSize: "11px",
                    fontWeight: 700,
                    textTransform: "uppercase",
                    letterSpacing: "0.8px",
                    color: dt.textMuted,
                    fontFamily: dt.font,
                    whiteSpace: "nowrap",
                    mt: "2px",
                    minWidth: LABEL_WIDTH,
                    flexShrink: 0,
                  }}
                >
                  {translate("servicing:card.completionReport")}
                </Box>
                <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: "4px 12px", minWidth: 0 }}>
                  {item.acceptedOffer.workCompletionFiles.map((file, idx) => (
                    <Box
                      key={idx}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: "4px",
                        transition: "all 0.15s ease",
                      }}
                    >
                      {downloadingFile === file.name ? (
                        <CircularProgress size={12} sx={{ color: "#27AE60", flexShrink: 0 }} />
                      ) : (
                        <FileIcon sx={{ fontSize: 14, color: "#27AE60", flexShrink: 0 }} />
                      )}
                      <Box
                        onClick={(e) => handleCompletionFileView(e, file.name)}
                        sx={{
                          fontSize: "13px",
                          fontWeight: 500,
                          color: "#27AE60",
                          fontFamily: dt.font,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          minWidth: 0,
                          cursor: downloadingFile === file.name ? "wait" : "pointer",
                          textDecoration: "none",
                          "&:hover": { textDecoration: "underline" },
                        }}
                      >
                        {file.name}
                      </Box>
                      <Box sx={{ fontSize: "10px", color: dt.textMuted, fontFamily: dt.fontMono, flexShrink: 0 }}>
                        {formatFileSize(file.size)}
                      </Box>
                    </Box>
                  ))}
                </Box>
              </Box>
            )}
          </Box>

        {/* Footer section (inside left zone, not full width!) */}
        <Box
          sx={{
            borderTop: `1px solid ${dt.cardBorder}`,
            backgroundColor: "#F8FAFC",
            padding: isPhone ? "12px 16px" : "12px 24px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: "16px",
            flexWrap: "wrap",
            mt: "auto",
          }}
        >
          {/* CREATED */}
          <Box sx={{ minWidth: 0 }}>
            <Box sx={{ fontSize: "10px", fontWeight: 500, textTransform: "uppercase", letterSpacing: "0.8px", color: dt.textMuted, fontFamily: dt.font, mb: "2px" }}>
              {translate("servicing:card.created")}
            </Box>
            <Box sx={{ fontSize: "13px", fontWeight: 500, color: "#1E293B", fontFamily: dt.font }}>
              {item.createdDate ? formatUiDate(item.createdDate) : "\u2014"}
            </Box>
          </Box>

          {/* SERVICE DATE / START OF WORK */}
          <Box sx={{ minWidth: 0 }}>
            <Box sx={{ fontSize: "10px", fontWeight: 500, textTransform: "uppercase", letterSpacing: "0.8px", color: dt.textMuted, fontFamily: dt.font, mb: "2px" }}>
              {hasAcceptedOffer && item.acceptedOffer?.confirmedArrivalDate
                ? translate("servicing:card.startOfWork")
                : translate("servicing:card.serviceDate")}
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: "4px" }}>
              <ScheduleOutlined sx={{ fontSize: "14px", color: dt.textMuted }} />
              <Box sx={{ fontSize: "13px", fontWeight: 500, color: "#1E293B", fontFamily: dt.font }}>
                {serviceDateInfo.text}
                {serviceDateInfo.timeWindow && (
                  <Box component="span" sx={{ color: dt.textMuted, fontFamily: dt.fontMono, fontSize: "12px" }}>
                    {" "}&middot; {serviceDateInfo.timeWindow}
                  </Box>
                )}
              </Box>
            </Box>
          </Box>

          {/* WORK COMPLETED (only when provider marked work as done) */}
          {item.acceptedOffer?.workCompletedDate && (
            <Box sx={{ minWidth: 0 }}>
              <Box sx={{ fontSize: "10px", fontWeight: 500, textTransform: "uppercase", letterSpacing: "0.8px", color: dt.textMuted, fontFamily: dt.font, mb: "2px" }}>
                {translate("servicing:card.workCompleted")}
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: "4px" }}>
                <AssignmentTurnedInOutlined sx={{ fontSize: "14px", color: "#059669" }} />
                <Box sx={{ fontSize: "13px", fontWeight: 500, color: "#1E293B", fontFamily: dt.font }}>
                  {formatUiDate(item.acceptedOffer.workCompletedDate)}
                  <Box component="span" sx={{ color: dt.textMuted, fontFamily: dt.fontMono, fontSize: "12px" }}>
                    {" "}&middot; {dayjs(item.acceptedOffer.workCompletedDate).format("HH:mm")}
                  </Box>
                </Box>
              </Box>
            </Box>
          )}

          {/* OFFERS count badge (for open/negotiating stages) */}
          {!hasAcceptedOffer && offersCount > 0 && !isArchived && (
            <Box sx={{ minWidth: 0 }}>
              <Box sx={{ fontSize: "10px", fontWeight: 500, textTransform: "uppercase", letterSpacing: "0.8px", color: dt.textMuted, fontFamily: dt.font, mb: "2px" }}>
                {translate("servicing:offers")}
              </Box>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: "28px",
                  height: "28px",
                  borderRadius: "50%",
                  fontSize: "12px",
                  fontWeight: 700,
                  fontFamily: dt.fontMono,
                  border: `2px solid ${dt.blue}`,
                  color: dt.blue,
                  backgroundColor: dt.blueSoft,
                  ...(newOffersCount > 0 && { animation: "offerRing 2.5s ease-in-out infinite" }),
                }}
              >
                {offersCount}
              </Box>
            </Box>
          )}

          {/* PRICE (BUDGET or AGREED PRICE) */}
          <Box sx={{ minWidth: 0, textAlign: "right", ml: "auto" }}>
            <Box sx={{ fontSize: "10px", fontWeight: 500, textTransform: "uppercase", letterSpacing: "0.8px", color: dt.textMuted, fontFamily: dt.font, mb: "2px" }}>
              {priceInfo.label}
            </Box>
            <Box sx={{ fontSize: "20px", fontWeight: 700, fontFamily: dt.fontMono, color: "#1E293B" }}>
              {priceInfo.value}
            </Box>
          </Box>
        </Box>
      </Box>

      {/* ================================================================
          RIGHT ZONE: horizontal stepper, context panel, compact CTA
          ================================================================ */}
      <Box
        sx={{
          flex: isPhone ? "none" : "3",
          display: "flex",
          flexDirection: "column",
          padding: "20px",
          borderLeft: isPhone ? "none" : `1px solid ${dt.cardBorder}`,
          borderTop: isPhone ? `1px solid ${dt.cardBorder}` : "none",
          gap: "12px",
          transition: "flex 0.3s ease",
        }}
      >
        {/* PROGRESS — horizontal stepper (desktop) or compact dots (phone) */}
        {item.statusCode !== S.archived && (
          <Box>
            <Box
              sx={{
                fontSize: "11px",
                fontWeight: 700,
                textTransform: "uppercase",
                letterSpacing: "1px",
                color: dt.textMuted,
                fontFamily: dt.font,
                mb: "6px",
              }}
            >
              {translate("servicing:card.progress")}
            </Box>
            {!isPhone ? (
              <ServiceLifecycleTracker
                stages={ownerStages}
                variant="card-horizontal"
              />
            ) : (
              <ServiceLifecycleTracker
                stages={ownerStages}
                variant="compact"
              />
            )}
          </Box>
        )}

        {/* STATUS CONTEXT STRIP — color-coded guidance + per-stage data rows */}
        <Box sx={{ display: "flex", flexDirection: "column", gap: "10px", flex: 1 }}>
          {/* Color-coded guidance box */}
          {guidance && (
            <ContextGuidanceBox
              text={guidance.text}
              boldName={guidance.boldName}
              category={getOwnerGuidanceCategory(item.statusCode, offersCount)}
            />
          )}

          {/* Time-in-stage indicator */}
          {timeAgoStr && (
            <TimeInStageRow
              label={translate(timeIndicator.labelKey)}
              timeAgo={timeAgoStr}
            />
          )}

          {/* ── Per-stage contextual data rows (max 3) ── */}

          {/* Offers count + lowest price (open/negotiating stages) */}
          {!hasAcceptedOffer && offersCount > 0 && !isArchived && !isPhone && (
            <ContextInfoRow
              icon={<LocalOfferOutlined sx={{ fontSize: "14px", color: dt.blue }} />}
            >
              <Box component="span" sx={{ fontFamily: dt.fontMono, fontWeight: 600 }}>
                {item.lowestOfferPrice != null
                  ? translate("servicing:card.contextRows.offersFromPrice", {
                      count: offersCount,
                      price: formatCurrency(item.lowestOfferPrice),
                    })
                  : translate("servicing:card.offerCount", { count: offersCount })}
              </Box>
            </ContextInfoRow>
          )}

          {/* Budget vs lowest offer (negotiating — killer feature) */}
          {item.statusCode === S.negotiating && item.budgetEnabled && item.lowestOfferPrice != null && !isPhone && (() => {
            const budgetStr = item.budgetTypeCode === 1 && item.fixedBudget
              ? formatCurrency(item.fixedBudget)
              : item.budgetTypeCode === 2 && item.budgetRangeFrom && item.budgetRangeTo
              ? `${formatCurrency(item.budgetRangeFrom)}\u2013${formatCurrency(item.budgetRangeTo)}`
              : null;
            if (!budgetStr) return null;
            return (
              <ContextInfoRow
                icon={<LocalOfferOutlined sx={{ fontSize: "14px", color: dt.textMuted }} />}
              >
                <Box component="span" sx={{ fontFamily: dt.fontMono, fontWeight: 500 }}>
                  {translate("servicing:card.contextRows.budgetVsOffer", {
                    budget: budgetStr,
                    lowest: formatCurrency(item.lowestOfferPrice),
                  })}
                </Box>
              </ContextInfoRow>
            );
          })()}

          {/* Service date + time window (confirmed/working) */}
          {hasAcceptedOffer && item.acceptedOffer?.confirmedArrivalDate &&
            [S.awaitingProviderConfirmation, S.inProgress, S.awaitingCompletionConfirmation].includes(item.statusCode) && (
            <ContextInfoRow
              icon={<CalendarTodayOutlined sx={{ fontSize: "14px", color: dt.textMuted }} />}
            >
              <Box component="span" sx={{ fontFamily: dt.fontMono, fontWeight: 600 }}>
                {formatUiDate(item.acceptedOffer.confirmedArrivalDate)}
                {item.acceptedOffer.confirmedArrivalTimeFrom && ` \u00B7 ${item.acceptedOffer.confirmedArrivalTimeFrom.slice(0, 5)}`}
                {item.acceptedOffer.confirmedArrivalTimeTo && `\u2013${item.acceptedOffer.confirmedArrivalTimeTo.slice(0, 5)}`}
              </Box>
            </ContextInfoRow>
          )}

          {/* Provider info (when accepted) */}
          {hasAcceptedOffer && !isPhone && (
            <Box sx={{ display: "flex", alignItems: "center", gap: "6px" }}>
              {item.acceptedOffer?.providerLogoUrl ? (
                <Box
                  component="img"
                  src={item.acceptedOffer.providerLogoUrl}
                  alt=""
                  sx={{
                    width: "20px",
                    height: "20px",
                    borderRadius: "50%",
                    objectFit: "cover",
                    flexShrink: 0,
                    border: `1px solid ${dt.cardBorder}`,
                  }}
                />
              ) : (
                <Box
                  sx={{
                    width: "20px",
                    height: "20px",
                    borderRadius: "50%",
                    backgroundColor: dt.blueSoft,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  <Box sx={{ fontSize: "10px", fontWeight: 700, color: dt.blue, fontFamily: dt.font }}>
                    {item.acceptedOffer?.providerName?.charAt(0)?.toUpperCase() || "?"}
                  </Box>
                </Box>
              )}
              <Box sx={{ fontSize: "12px", fontFamily: dt.font, color: dt.textSecondary, fontWeight: 500 }}>
                {item.acceptedOffer?.providerName}
              </Box>
              {item.acceptedOffer?.providerAverageRating != null && (
                <Box sx={{ fontSize: "11px", fontFamily: dt.fontMono, color: dt.textMuted }}>
                  {translate("servicing:card.contextRows.providerRating", {
                    rating: item.acceptedOffer.providerAverageRating.toFixed(1),
                    count: item.acceptedOffer.providerReviewCount ?? 0,
                  })}
                </Box>
              )}
            </Box>
          )}

          {/* Agreed price (post-acceptance stages) */}
          {hasAcceptedOffer && item.acceptedOffer?.totalEstimatedCost && !isPhone &&
            [S.awaitingProviderConfirmation, S.inProgress, S.awaitingCompletionConfirmation].includes(item.statusCode) && (
            <ContextInfoRow
              icon={<LocalOfferOutlined sx={{ fontSize: "14px", color: dt.textMuted }} />}
            >
              <Box component="span" sx={{ fontFamily: dt.fontMono, fontWeight: 600 }}>
                {translate("servicing:card.contextRows.agreedPrice", {
                  price: formatCurrency(item.acceptedOffer.totalEstimatedCost),
                })}
              </Box>
            </ContextInfoRow>
          )}

          {/* Work completion note (awaiting completion confirmation) */}
          {item.statusCode === S.awaitingCompletionConfirmation && item.acceptedOffer?.workCompletionNote && !isPhone && (
            <ContextInfoRow
              icon={<FileIcon sx={{ fontSize: "14px", color: "#E67E22" }} />}
              color="#9A3412"
            >
              <Box component="span" sx={{
                overflow: "hidden",
                display: "-webkit-box",
                WebkitLineClamp: 1,
                WebkitBoxOrient: "vertical",
              }}>
                {translate("servicing:card.contextRows.completionNote", {
                  note: item.acceptedOffer.workCompletionNote.slice(0, 60) + (item.acceptedOffer.workCompletionNote.length > 60 ? "..." : ""),
                })}
              </Box>
            </ContextInfoRow>
          )}

          {/* Completion files count (awaiting completion confirmation) */}
          {item.statusCode === S.awaitingCompletionConfirmation && item.acceptedOffer?.workCompletionFiles && item.acceptedOffer.workCompletionFiles.length > 0 && !isPhone && (
            <ContextInfoRow
              icon={<FileIcon sx={{ fontSize: "14px", color: dt.textMuted }} />}
            >
              {translate("servicing:card.contextRows.completionFiles", { count: item.acceptedOffer.workCompletionFiles.length })}
            </ContextInfoRow>
          )}
        </Box>

        {/* CTA button — compact (auto-width, centered) */}
        {cta && (
          <Box sx={{ mt: "auto", display: "flex", justifyContent: "center" }}>
            <CTAButton {...cta} compact />
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default ServiceRequestCard;
